import { observable, action } from 'mobx';
import { Value, Inline, Block } from 'slate';
import XRegExp from 'xregexp';
import uuid from 'uuid/v4';

import FileAppManager from 'apps/file/appManager';

import {
  pluginPresets,
  buttonPresets,
  mediaPresets,
} from 'libs/editor/plugins/presets';

import { patterns } from 'libs/editor/patterns';
import { CurrentWord } from 'libs/editor/plugins/helpers/currentWord';
import { Autocomplete } from 'libs/editor/views/autocomplete';
// import { Spellcheck } from 'libs/editor/plugins/features/spellcheck';

// TODO - Move autocomplete imports to separate lib/app
import path from 'path';
import objectPath from 'object-path';
import { throttle, debounce } from 'throttle-debounce';

import { client, routerStore } from 'core';
import { analytics } from 'core/analytics';

import { clientStore } from 'apps/client/app';
import { AUTOCOMPLETE_PROFILES_CONTAIN } from 'apps/profile/gql';
import { AUTOCOMPLETE_INITIATIVES } from 'apps/initiative/gql';
import {
  AUTOCOMPLETE_PROFILE_PROJECTS,
  AUTOCOMPLETE_INITIATIVE_PROJECTS,
} from 'apps/project/gql';
import { AUTOCOMPLETE_REPO_FILES } from 'apps/repository/gql';
import { AUTOCOMPLETE_TAGS_CONTAINS } from 'apps/tag/gql';

import { schema } from 'libs/editor/schema';
import { markdown } from 'libs/editor/serializers/markdown';

import { videoInfoFromURL } from 'libs/editor/views/mediaInput';

import {
  createTable,
  tableOptions,
} from 'libs/editor/plugins/nodes/blocks/table';

const AUTOCOMPLETE_MAX_RESULTS = 5;

const defaultValue = {
  document: {
    nodes: [
      {
        object: 'block',
        type: 'paragraph',
        nodes: [
          {
            object: 'text',
            leaves: [
              {
                text: '',
              },
            ],
          },
        ],
      },
    ],
  },
};

export class MediaMenuStore {
  @observable selected;
  @observable inputActive;
  @observable inputValue;
  @observable component;
  buttons;
  constructor(buttons = []) {
    this.selected = false;
    this.buttons = buttons;
    this.inputActive = false;
    this.inputValue = '';
    this.component = undefined;
  }

  @action SetSelected = selected => {
    this.selected = selected;
  };

  @action SetInput = value => {
    this.inputValue = value || '';
  };

  @action SetInputStatus = status => {
    this.inputActive = status;
  };
}

export class ToolbarStore {
  @observable inputActive;
  @observable inputValue;
  @observable component;
  buttons;
  constructor(buttons = []) {
    this.buttons = buttons;
    this.inputActive = false;
    this.inputValue = '';
    this.component = undefined;
  }

  @action SetInput = value => {
    this.inputValue = value || '';
  };

  @action SetInputStatus = status => {
    this.inputActive = status;
  };
}

export class TableStore {
  @observable inputActive;
  @observable inputValue;
  buttons;
  constructor(buttons = []) {
    this.buttons = buttons;
    this.inputActive = false;
    this.inputValue = '';
  }

  @action SetInput = value => {
    this.inputValue = value || '';
  };

  @action SetInputStatus = status => {
    this.inputActive = status;
  };
}

class AutocompleteStore {
  @observable type;
  @observable matches;
  @observable complete;
  @observable selectedItem;
  @observable visible;
  input;
  position;
  userAction;
  constructor(type = 'mention') {
    this.matches = [];
    this.complete = '';
    this.selectedItem = 0;
    this.visible = false;
    this.autocompleteDebounced = debounce(500, this.autocompleteQuery);
    this.autocompleteThrottled = throttle(500, this.autocompleteQuery);
    this.type = type;
    this.position = null;
    this.userAction = false;
  }

  @action SelectItem = i => {
    const filtered = Math.min(Math.max(i, 0), this.matches.length - 1);
    this.selectedItem = filtered;
  };

  @action Discard = () => {
    this.visible = false;
    this.matches = [];
    this.selectedItem = 0;
    this.position = null;
    this.userAction = true;
  };

  @action SetMatches(matches) {
    this.matches = matches;
    this.selectedItem = 0;
    this.visible = true;
  }

  autocompleteQuery = params => {
    client.query(params).then(res => {
      if (res.data) {
        const matches = objectPath
          .get(res.data, params.path, [])
          .slice(0, AUTOCOMPLETE_MAX_RESULTS);
        this.SetMatches(matches);
      }
    });
  };

  paramsFromMatches = matches => {
    const params = {
      variables: { first: AUTOCOMPLETE_MAX_RESULTS },
      query: null,
    };
    const groups = matches;
    const filepath = groups.relativepath || groups.path;
    if (filepath) {
      params.query = AUTOCOMPLETE_REPO_FILES;
      params.path = 'project.result.contribution.files';
      // FIXME - Find a better way of extracting the current space and slug
      const currentPath = routerStore.route.path.split('/').filter(e => !!e);
      const space = groups.username || currentPath[0];
      const slug = groups.project || currentPath[1];
      const dirname = path.dirname(filepath);
      const basepath = dirname === '.' ? '' : dirname;
      const complete = path.basename(filepath);
      params.variables = {
        space,
        slug,
        basepath,
        complete,
      };
    } else if (groups.project) {
      const symbol = groups.username[0];
      switch (symbol) {
        case '+':
          params.query = AUTOCOMPLETE_INITIATIVE_PROJECTS;
          params.path = 'initiative.result.projects.edges';
          break;
        case '@':
        default:
          params.query = AUTOCOMPLETE_PROFILE_PROJECTS;
          params.path = 'profile.result.projects.edges';
          break;
      }
      params.variables = {
        username: groups.username,
        complete: groups.project,
      };
    } else if (groups.username) {
      const symbol = groups.username[0];
      switch (symbol) {
        case '+':
          params.query = AUTOCOMPLETE_INITIATIVES;
          params.path = 'initiatives.result.edges';
          break;
        case '@':
        default:
          params.query = AUTOCOMPLETE_PROFILES_CONTAIN;
          params.path = 'profiles.result.edges';
          break;
      }
      params.variables.complete = groups.username.substring(1);
    } else if (groups.tag) {
      params.query = AUTOCOMPLETE_TAGS_CONTAINS;
      params.path = 'channels.result.edges';
      params.variables.complete = groups.tag.substring(1);
    }
    return params;
  };

  checkInput = ({ word, range }) => {
    const input = word;
    if (input.trim().length === 0) {
      this.Discard();
    }
    if (this.userAction) {
      this.userAction = false;
      return;
    }
    if (input === this.input) {
      return;
    }
    this.input = input;
    const pattern = patterns[this.type];
    const matches = XRegExp.exec(input, pattern);
    if (matches) {
      this.position = range;
      const queryParams = this.paramsFromMatches(matches);
      const { complete } = queryParams.variables;
      this.complete = complete;
      if (complete.length < 5) {
        this.autocompleteThrottled(queryParams);
        return;
      }
      this.autocompleteDebounced(queryParams);
      return;
    }
    this.Discard();
  };
}

export class EditorStore {
  @observable uuid;
  @observable value;
  @observable markdownValue;
  @observable target;
  @observable changeCallback;
  @observable readOnly;
  @observable formatting;
  @observable showMediaMenu;
  @observable spellCheck;
  @observable placeholder;
  @observable hasError;
  @observable selection;
  @observable reference;
  @observable component;
  @observable toolbarAlwaysVisible;
  @observable mediaMenuAlwaysVisible;
  plugins;
  mediaMenuStore;
  toolbarStore;
  tableStore;
  autocompleteStore;
  previousValue;
  restored;
  embedDisabled;
  associatedSpaceId;
  constructor(options = {}) {
    this.hasError = false;
    this.restored = false;
    this.uuid = uuid();
    const value = options.initialValue || Value.fromJSON(defaultValue);
    this.previousValue = value.toJSON();
    this.schema = schema;
    this.value = value;
    this.formatting = options.formatting || 'none';
    this.showMediaMenu = options.showMediaMenu || false;
    this.readOnly = options.readOnly !== false;
    // FIXME - Enable spellcheck by default until issue with plugin is solved
    this.spellCheck = !this.readOnly;
    this.changeCallback = options.onChange || null;
    this.placeholder = options.placeholder || '';
    this.target = options.target || null;
    this.plugins = pluginPresets[this.formatting];
    this.embedDisabled = options.embedDisabled;
    this.toolbarAlwaysVisible = options.toolbarAlwaysVisible || false;
    this.mediaMenuAlwaysVisible = options.mediaMenuAlwaysVisible || false;
    this.mediaMenuStore =
      mediaPresets[this.formatting] &&
      new MediaMenuStore(mediaPresets[this.formatting]);

    this.toolbarStore =
      buttonPresets[this.formatting] &&
      new ToolbarStore(buttonPresets[this.formatting]);

    this.tableStore =
      buttonPresets[this.formatting] &&
      new TableStore(buttonPresets[this.formatting]);

    this.autocompleteStore = new AutocompleteStore('mention');
    this.plugins = [...this.plugins, Autocomplete(this)];
    this.markdownValue = '';

    this.associatedSpaceId = options.associatedSpaceId || undefined;
  }

  @action SetSelection = selection => {
    if (this.mediaMenuStore) {
      const currentTop = this.selection && this.selection.top;
      const incomingTop = selection && selection.top;
      if (currentTop !== incomingTop) {
        this.mediaMenuStore.SetSelected(false);
      }
    }
    this.selection = selection;
  };

  @action SetReference = reference => {
    this.reference = reference;
  };

  @action SetComponent = component => {
    this.component = component;
  };

  @action CatchError = (error, info) => {
    this.hasError = true;
    const scopingFunction = scope => {
      try {
        scope.setExtra('value', JSON.stringify(this.value.toJSON()));
        scope.setExtra('previousValue', this.previousValue);
        scope.setExtra('info', info);
      } catch (e) {
        console.error('An error ocurred while handling an error!');
      }
    };
    analytics.logError(error, scopingFunction);
  };

  @action RestorePreviousValue = () => {
    this.restored = true;
    this.value = Value.fromJSON(this.previousValue);
    this.hasError = false;
  };

  @action SetEditable = editable => {
    this.readOnly = !editable;
    // If message is editable focus textarea
    if (!this.readOnly && this.reference) {
      this.reference.focus();
    }
  };

  @action SetSpellCheck = enabled => {
    this.spellCheck = enabled;
  };

  @action ResetValue = () => {
    this.value = Value.fromJSON(defaultValue);
  };

  @action onChangeTextarea = e => {
    const content = e.target.value;
    const value = markdown.deserialize(content);
    this.markdownValue = content;
    this.onChange({ value });
  };

  @action setMarkdownValue = () => {
    try {
      this.markdownValue = markdown.serialize(this.value);
    } catch (firstError) {
      const firstScope = scope => {
        scope.setLevel('warning');
      };
      analytics.logError(firstError, firstScope);
      try {
        this.reference.controller.normalize();
        this.markdownValue = markdown.serialize(
          this.reference.controller.value,
        );
      } catch (secondError) {
        analytics.logError(secondError);
        this.markdownValue = '';
      }
    }
  };

  @action onChange = ({ value }) => {
    this.previousValue = this.value.toJSON();
    this.value = value;
    this.restored = false;
    if (this.changeCallback) this.changeCallback(value);
  };

  // FIXME - don't reuse file
  // eslint-disable-next-line no-shadow
  @action UploadFile = file => {
    let formTarget = this.target;

    // FIXME - temporary solution for editing comments
    if (formTarget?.constructor?.name === 'CommentApp') {
      formTarget = undefined;
    }

    const fileManager = new FileAppManager(null, {
      formTarget,
      targetSpaceId: this.associatedSpaceId
        ? this.associatedSpaceId()
        : undefined,
    });

    const { reference, value } = this;
    const { anchorBlock } = value;
    const type =
      file.type && file.type.startsWith('image')
        ? 'image-block'
        : 'preview-block';
    const data = { fileManager };
    const fileTemplate = { type, nodes: [], data };

    if (type === 'image-block') {
      data.src = window.URL.createObjectURL(file);
      data.alt = 'image';
    } else {
      data.alt = `View ${file.name} @ Wikifactory`;
    }

    const imageNode = Block.create(fileTemplate);

    clientStore.uploader.FastTrackFile(
      fileManager,
      {
        fileObject: file,
      },
      () => {
        if (type === 'image-block') {
          fileTemplate.data.src = fileManager.file.permalink;
        } else {
          fileTemplate.data.href = `${MAIN_URL}${fileManager.file.viewerPath}`;
        }
        const updatedNode = Block.create(fileTemplate);
        analytics.sendEvent('file-embed-upload');
        try {
          reference.replaceNodeByKey(imageNode.key, updatedNode);
          this.onChange(reference);
        } catch (err) {
          console.error(err);
        }
      },
    );

    reference.insertBlock('paragraph');
    const paragraphNode = reference.value.anchorBlock;
    reference.replaceNodeByKey(anchorBlock.key, imageNode);
    reference.moveToRangeOfNode(paragraphNode).moveToEnd().focus();
    this.onChange(reference);
  };

  @action SubmitVideo = () => {
    const { inputValue } = this.mediaMenuStore;
    const videoInfo = videoInfoFromURL(inputValue);
    if (videoInfo) {
      const { reference, value } = this;
      const { anchorBlock } = value;
      const { key, site } = videoInfo;
      const data = { src: key, site };
      const videoTemplate = {
        type: 'video-block',
        data,
      };
      const videoNode = Block.create(videoTemplate);
      reference.insertBlock('paragraph');
      const paragraphNode = reference.value.anchorBlock;
      reference.replaceNodeByKey(anchorBlock.key, videoNode);
      reference.moveToRangeOfNode(paragraphNode).moveToEnd().focus();
      this.onChange(reference);
    }
    this.mediaMenuStore.SetInput();
    this.mediaMenuStore.SetInputStatus(false);
  };

  @action InsertPreview = (href, filename) => {
    const { reference } = this;
    const alt = `View ${filename} @ Wikifactory`;
    const previewTemplate = {
      type: 'preview',
      data: { href: encodeURI(decodeURI(href)), alt },
    };
    analytics.sendEvent('file-embed');
    const previewNode = Inline.create(previewTemplate);
    reference.insertInline(previewNode);
    this.onChange(reference);
  };

  @action PickSuggestion = () => {
    const item =
      this.autocompleteStore.matches[this.autocompleteStore.selectedItem];
    if (item) {
      const { word, range } = CurrentWord(this.value);
      const profile =
        item.node &&
        (item.node.username || item.node.slug || `#${item.node.name}`);
      const text = profile || item.filename;
      const { reference } = this;
      const newChange = reference
        .moveTo(range.anchor.key, range.anchor.offset)
        .deleteForward(word.length)
        .insertText(text);
      this.autocompleteStore.input = text;
      this.autocompleteStore.Discard();
      this.onChange(newChange);
    }
  };

  @action InsertTable = () => {
    const { reference, value } = this;
    // reference is here the editor with the plugins attached

    if (!value.selection.start.key) return false;

    const table = createTable(tableOptions, 3, 3, {});

    reference.insertBlock(table);

    this.onChange(reference);
    return reference;
  };
}
